// Member API endpoints
import apiClient from './apiClient';
import type {
  User,
  MemberProfile,
  SocialLinks,
  PreferencesRequest,
  Tag,
  TagCategory,
  Reservation,
  Booking,
  GiftCard,
  MemberTypes,
  MaritalStatus,
  MemberFriend,
  PublicMemberProfile,
  ReviewRequest,
  MemberStats,
} from '@/types/api';

// Helper to extract data from wrapped response
// Handles various API response formats:
// - Direct response: { id, name, email, ... }
// - Wrapped response: { data: { id, name, email, ... } }
// - With success flag: { success: true, data: { ... } }
function unwrapResponse<T>(response: unknown): T {
  if (response === null || response === undefined) {
    return response as T;
  }

  if (typeof response !== 'object') {
    return response as T;
  }

  const obj = response as Record<string, unknown>;

  // Check for error response
  if ('success' in obj && obj.success === false) {
    const message = (obj.message as string) || 'Request failed';
    throw new Error(message);
  }

  // If response has a 'data' property, unwrap it
  if ('data' in obj && obj.data !== undefined) {
    // Check for double-wrapped: { data: { data: ... } }
    const innerData = obj.data as Record<string, unknown>;
    if (typeof innerData === 'object' && innerData !== null && 'data' in innerData) {
      return innerData.data as T;
    }
    return obj.data as T;
  }

  // Return as-is (direct response or has success: true with data at top level)
  return response as T;
}

export function publicMemberTagsToSelectedTags(
  tags: PublicMemberProfile['tags'] | undefined,
): Record<string, string[]> {
  if (!tags || typeof tags !== 'object') return {};
  const out: Record<string, string[]> = {};
  for (const [categoryKey, bucket] of Object.entries(tags)) {
    const items = bucket?.items;
    if (!Array.isArray(items)) continue;
    out[categoryKey] = items
      .map((i) => (i && typeof i.name === 'string' ? i.name : ''))
      .filter(Boolean);
  }
  return out;
}

/** Guest profile strip uses the same keys as `socialLinks` state in UserProfile. */
export function publicMemberSocialToGuestLinks(
  social: PublicMemberProfile['social'] | undefined,
): {
  instagram: string;
  linkedin: string;
  facebook: string;
  tiktok: string;
  snapchat: string;
  youtube: string;
  twitter: string;
} {
  const empty = {
    instagram: '',
    linkedin: '',
    facebook: '',
    tiktok: '',
    snapchat: '',
    youtube: '',
    twitter: '',
  };
  if (!social || typeof social !== 'object') return empty;
  const s = social as Record<string, unknown>;
  const str = (k: string) => {
    const v = s[k];
    return typeof v === 'string' && v.trim() ? v : '';
  };
  return {
    instagram: str('instagram'),
    linkedin: str('linkedin'),
    facebook: str('facebook'),
    tiktok: str('tiktok'),
    snapchat: str('snapchat'),
    youtube: str('youtube'),
    twitter: str('twitter'),
  };
}

export type MemberTransaction = {
  id: string;
  amount: number;
  currency?: string | null;
  model?: string | null;
  paymentMethod?: string | null;
  status?: string | null;
  refundedAmount?: number | null;
  created_at: string;
  invoice?: string | null;
  venue?: { id: string; name: string; slug?: string; logo?: string | null } | null;
};

export type FavoriteMember = {
  userId: string;
  name: string;
  username: string | null;
  avatar: string | null;
  location: string | null;
};

export type FavoriteEvent = {
  id: string;
  slug: string;
  name: string;
  date: string | null;
  image: string | null;
  venueName: string | null;
  venueSlug: string | null;
};

export const memberApi = {
  /**
   * Public member profile by username (GET /member/:username).
   *
   * Guests can view it without a token, but the request deliberately keeps the bearer
   * header when the viewer is signed in: the API uses it to hide the profile from
   * members who blocked each other, and to answer with a `blockedByViewer` stub when
   * *I* blocked this member (so the UI can offer Unblock).
   */
  getPublicMemberByUsername: async (username: string): Promise<PublicMemberProfile> => {
    const u = encodeURIComponent(username.trim());
    const rawResponse = await apiClient.get<PublicMemberProfile | { data: PublicMemberProfile }>(
      `/member/${u}`,
    );
    return unwrapResponse<PublicMemberProfile>(rawResponse);
  },

  /** Claim an inactive profile — emails a set-password link to the address on file. */
  claimProfile: async (username: string): Promise<{ message?: string }> => {
    return apiClient.post<{ message?: string }>(`/member/${encodeURIComponent(username.trim())}/claim`, {});
  },

  /**
   * Get current member info
   */
  getInfo: async (): Promise<User> => {
    const rawResponse = await apiClient.get<User | { data: User }>('/member', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Current member session (`GET /me`) — includes `completed` for onboarding gate
   */
  getMe: async (): Promise<User> => {
    const rawResponse = await apiClient.get<User | { data: User }>('/me', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Lightweight profile header counts — avoids loading full lists just to show numbers.
   */
  getStats: async (): Promise<MemberStats> => {
    const rawResponse = await apiClient.get<MemberStats | { data: MemberStats }>('/member/stats', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Update member info
   *
   * Changing `email` or `phone` invalidates that value's verification server-side (and re-mails the
   * activation link), so the response also reports which login value must be verified next.
   */
  updateInfo: async (data: {
    name: string;
    email: string;
    phone: string;
    latitude?: string;
    longitude?: string;
    timezone?: string;
    birthdate?: string;
    gender?: string;
    address?: string;
    country?: string;
    state?: string;
    zipcode?: string;
    member?: MemberProfile;
  }): Promise<{
    user: User;
    requiresEmailVerification: boolean;
    requiresPhoneVerification: boolean;
  }> => {
    const rawResponse = await apiClient.put<User | { data: User }>('/member/update-info', data, { requireAuth: true });
    const root = (rawResponse ?? {}) as {
      requiresEmailVerification?: boolean;
      requiresPhoneVerification?: boolean;
    };
    return {
      user: unwrapResponse<User>(rawResponse),
      requiresEmailVerification: root.requiresEmailVerification === true,
      requiresPhoneVerification: root.requiresPhoneVerification === true,
    };
  },

  /**
   * Update member social links
   */
  updateSocial: async (social: SocialLinks): Promise<{ message: string }> => {
    return apiClient.put('/member/update-social', { social }, { requireAuth: true });
  },

  /** Set (base64 data-URI) or remove (null) the profile cover image. */
  updateCover: async (cover: string | null): Promise<{ cover: string | null }> => {
    return apiClient.put('/member/update-cover', { cover }, { requireAuth: true });
  },

  /**
   * Update member avatar
   */
  updateAvatar: async (avatar: string): Promise<{ message: string; avatar: string }> => {
    return apiClient.put('/member/update-avatar', { avatar }, { requireAuth: true });
  },

  /**
   * Get member preferences/tags
   */
  getPreferences: async (): Promise<TagCategory> => {
    const rawResponse = await apiClient.get<TagCategory | { data: TagCategory }>('/member/account-tags', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Update member preferences
   */
  /** Names of the member's starred top-pick preferences. */
  getTopPicks: async (): Promise<string[]> => {
    const raw = await apiClient.get<string[] | { data: string[] }>('/member/top-picks', { requireAuth: true });
    return unwrapResponse<string[]>(raw) ?? [];
  },

  /** Replace the starred top picks (tag names; must be among the member's saved preferences). */
  updateTopPicks: async (names: string[]): Promise<unknown> => {
    return apiClient.post('/member/top-picks', { names }, { requireAuth: true });
  },

  updatePreferences: async (preferences: PreferencesRequest): Promise<{ message: string }> => {
    return apiClient.post('/member/preferences', preferences, { requireAuth: true });
  },

  /** Member's own "Social Profile" tags (what they are), same shape as getPreferences. */
  getProfileTags: async (): Promise<TagCategory> => {
    const rawResponse = await apiClient.get<TagCategory | { data: TagCategory }>('/member/profile-tags', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /** Replace Social Profile tags for the sent sections (bucket → tag ids), plus marital status. */
  updateProfileTags: async (
    body: PreferencesRequest & { maritalStatusId?: number | null },
  ): Promise<{ success: boolean }> => {
    return apiClient.post('/member/profile-tags', body, { requireAuth: true });
  },

  /**
   * Get all available tags for preferences
   */
  getTags: async (): Promise<TagCategory> => {
    const rawResponse = await apiClient.get<TagCategory | { data: TagCategory }>('/member/tags');
    return unwrapResponse(rawResponse);
  },

  /** Same catalog plus each section's display title (`titles`: bucket → title) for dynamic sections. */
  getTagsWithTitles: async (): Promise<{ tags: TagCategory; titles: Record<string, string> }> => {
    const raw = await apiClient.get<{ data: TagCategory; titles?: Record<string, string> }>('/member/tags');
    return { tags: unwrapResponse<TagCategory>(raw), titles: raw?.titles ?? {} };
  },

  /**
   * Get member types
   */
  getTypes: async (): Promise<MemberTypes[]> => {
    const rawResponse = await apiClient.get<MemberTypes[] | { data: MemberTypes[] }>('/member/types');
    return unwrapResponse(rawResponse);
  },

  /**
   * Get marital status options
   */
  getMaritalStatuses: async (): Promise<MaritalStatus[]> => {
    const rawResponse = await apiClient.get<MaritalStatus[] | { data: MaritalStatus[] }>(
      '/member/marital-statuses',
    );
    return unwrapResponse(rawResponse);
  },

  /**
   * Continue registration with preferences. `face`/`right`/`left`/`down` are optional
   * data-URI face images for personal verification — omit all four to skip, or send all
   * four together (a partial set is rejected by the API).
   */
  continueRegister: async (
    data: PreferencesRequest & {
      accountTypeId?: number;
      face?: string;
      right?: string;
      left?: string;
      down?: string;
    }
  ): Promise<{ message: string; isSelfiVerified?: boolean }> => {
    return apiClient.post('/member/continue-register', data, { requireAuth: true });
  },

  /**
   * Submit selfie verification outside the registration flow (e.g. an already-registered
   * member completing verification later). All four poses are required — a partial set is
   * rejected by the API. Backed by `POST /member/selfie-verification`.
   */
  submitSelfieVerification: async (data: {
    face: string;
    right: string;
    left: string;
    down: string;
  }): Promise<{ success: boolean; isSelfiVerified: boolean }> => {
    return apiClient.post('/member/selfie-verification', data, { requireAuth: true });
  },

  /**
   * Get member reservations
   */
  getReservations: async (): Promise<Reservation[]> => {
    const rawResponse = await apiClient.get<Reservation[] | { data: Reservation[] }>('/member/reservations', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Get a single member reservation by id (used by emailed reservation deep links, e.g. ?id=...)
   */
  getReservation: async (id: string): Promise<Reservation> => {
    const rawResponse = await apiClient.get<Reservation | { data: Reservation }>(`/member/reservations/${id}`, { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Update a member reservation (including cancellation via reservationStatus)
   */
  updateReservation: async (
    reservationId: string,
    data: {
      referredBy?: string | null;
      clientName: string;
      clientEmail: string;
      guestsCount: number;
      clientRequest?: string | null;
      timeReservedTo?: string | null;
      reservationDate: string;
      reservationType?: string | null;
      clientBirthDate?: string | null;
      timeReservedFrom: string;
      clientPhoneNumber: string;
      reservationStatus: string;
      dateReserved?: string | null;
      slug: string;
    }
  ): Promise<{ message?: string }> => {
    return apiClient.put(`/member/reservations/${reservationId}`, data, { requireAuth: true });
  },

  /**
   * Get member bookings
   */
  getBookings: async (): Promise<Booking[]> => {
    const rawResponse = await apiClient.get<Booking[] | { data: Booking[] }>('/member/bookings', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Get a single member booking by id (used by emailed booking deep links, e.g. ?id=...)
   */
  getBooking: async (id: string): Promise<Booking> => {
    const rawResponse = await apiClient.get<Booking | { data: Booking }>(`/member/booking/${id}`, { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Update a member booking (including cancellation via bookingStatus)
   */
  updateBooking: async (
    bookingId: string,
    data: {
      guestsCount: number;
      clientBudget?: string;
      bookingStatus: string;
      bookingType: string;
      bookingDate: string;
      timeReservedFrom: string;
    }
  ): Promise<{ message?: string }> => {
    return apiClient.put(`/member/bookings/${bookingId}`, data, { requireAuth: true });
  },

  /**
   * Get member tickets
   */
  getTickets: async (): Promise<unknown[]> => {
    const rawResponse = await apiClient.get<unknown[] | { data: unknown[] }>('/member/tickets', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Get member bottle orders
   */
  getBottles: async (): Promise<unknown[]> => {
    const rawResponse = await apiClient.get<unknown[] | { data: unknown[] }>('/member/bottles', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Update a member bottle order (including cancellation via bookingStatus)
   */
  updateBottle: async (
    bottleOrderId: string,
    data: {
      clientName: string;
      clientEmail: string;
      clientGender?: string | null;
      clientAddress?: string | null;
      clientBirthDate?: string | null;
      clientPhoneNumber: string;
      guests: number;
      orderDate: string;
      orderTime: string;
      totalOrderPrice: string;
      bookingStatus: string;
      clientRequest?: string | null;
      referredBy?: string | null;
    }
  ): Promise<{ message?: string }> => {
    return apiClient.put(`/member/bottles/${bottleOrderId}`, data, { requireAuth: true });
  },

  /**
   * Delete a member bottle order
   */
  deleteBottle: async (bottleOrderId: string): Promise<{ message?: string }> => {
    return apiClient.delete(`/member/bottles/${bottleOrderId}`, { requireAuth: true });
  },

  /**
   * Get member gift cards
   */
  getGiftCards: async (): Promise<GiftCard[]> => {
    const rawResponse = await apiClient.get<GiftCard[] | { data: GiftCard[] }>('/member/gift-cards', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /** Member payment history (latest 50), from the `transactions` table. */
  getTransactions: async (): Promise<MemberTransaction[]> => {
    const raw = await apiClient.get<{ data?: { data?: MemberTransaction[] } }>('/member/transactions?limit=50', { requireAuth: true });
    return raw?.data?.data ?? [];
  },

  /**
   * Delete a member gift card
   */
  deleteGiftCard: async (giftCardId: string): Promise<{ message?: string }> => {
    return apiClient.delete(`/member/gift-cards/${giftCardId}`, { requireAuth: true });
  },

  /**
   * Get member favorite venues
   */
  /** Response may be flat rows `{ id, slug, name, image, logo, location, ... }` or legacy `{ id, venue }`. */
  getFavorites: async (): Promise<unknown[]> => {
    const rawResponse = await apiClient.get<unknown[] | { data: unknown[] }>('/member/favorites', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Member-authored venue reviews (if supported by the API)
   */
  getReviews: async (): Promise<unknown[]> => {
    const rawResponse = await apiClient.get<unknown[] | { data: unknown[] }>('/member/reviews', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Update a member-authored review
   */
  updateReview: async (reviewId: string, data: Partial<ReviewRequest>): Promise<unknown> => {
    const rawResponse = await apiClient.put<unknown>(`/member/reviews/${reviewId}`, data, { requireAuth: true });
    return unwrapResponse<unknown>(rawResponse);
  },

  /**
   * Delete a member-authored review
   */
  deleteReview: async (reviewId: string): Promise<{ message?: string }> => {
    return apiClient.delete(`/member/reviews/${reviewId}`, { requireAuth: true });
  },

  /**
   * Toggle venue favorite status
   */
  toggleFavorite: async (venueId: string): Promise<{ isFavorite: boolean }> => {
    return apiClient.post('/member/favorites/toggle-status', { venueId }, { requireAuth: true });
  },

  /** Ids of the service providers the member saved. */
  getFavoriteProviderIds: async (): Promise<string[]> => {
    const rawResponse = await apiClient.get('/member/favorite-providers', { requireAuth: true });
    return unwrapResponse<string[]>(rawResponse) ?? [];
  },

  toggleFavoriteProvider: async (serviceProviderId: string): Promise<{ isFavorite: boolean }> => {
    return apiClient.post('/member/favorite-providers/toggle-status', { serviceProviderId }, { requireAuth: true });
  },

  /** Members the viewer saved from their public profiles, newest first. */
  getFavoriteMembers: async (): Promise<FavoriteMember[]> => {
    const rawResponse = await apiClient.get('/member/favorite-members', { requireAuth: true });
    return unwrapResponse<FavoriteMember[]>(rawResponse) ?? [];
  },

  toggleFavoriteMember: async (userId: string): Promise<{ isFavorite: boolean }> => {
    return apiClient.post('/member/favorite-members/toggle-status', { userId }, { requireAuth: true });
  },

  /** Events the member saved from a venue's events tab, soonest first. */
  getFavoriteEvents: async (): Promise<FavoriteEvent[]> => {
    const rawResponse = await apiClient.get('/member/favorite-events', { requireAuth: true });
    return unwrapResponse<FavoriteEvent[]>(rawResponse) ?? [];
  },

  toggleFavoriteEvent: async (eventId: string): Promise<{ isFavorite: boolean }> => {
    return apiClient.post('/member/favorite-events/toggle-status', { eventId }, { requireAuth: true });
  },

  /**
   * List member friends (imported contacts), paginated server-side.
   */
  getFriends: async (params: {
    page?: number;
    limit?: number;
    search?: string;
  } = {}): Promise<{ data: MemberFriend[]; page: number; limit: number; total: number; totalPages: number }> => {
    const q = new URLSearchParams();
    q.set('page', String(params.page ?? 1));
    q.set('limit', String(params.limit ?? 20));
    if (params.search?.trim()) q.set('search', params.search.trim());

    const rawResponse = await apiClient.get(`/member/friends?${q.toString()}`, { requireAuth: true });
    const response = rawResponse as Record<string, unknown>;
    const data = unwrapResponse<MemberFriend[]>(rawResponse);

    const pg =
      response.pagination && typeof response.pagination === 'object'
        ? (response.pagination as Record<string, unknown>)
        : {};

    return {
      data,
      page: (pg.page as number) ?? params.page ?? 1,
      limit: (pg.limit as number) ?? params.limit ?? 20,
      total: (pg.total as number) ?? data.length,
      totalPages: (pg.totalPages as number) ?? 1,
    };
  },

  /**
   * Remove a friend connection
   */
  deleteFriend: async (friendId: number): Promise<{ message?: string }> => {
    return apiClient.delete(`/member/friends/${friendId}`, { requireAuth: true });
  },

  /**
   * Import contacts for invites
   */
  importContacts: async (data: {
    contacts: { name: string; email: string[]; phone: string[]; imageurl?: string }[];
    category: string;
    serviceName: string;
    listName: string;
  }): Promise<{ message: string }> => {
    return apiClient.post('/social-inviter-import', data, { requireAuth: true });
  },

  /**
   * Import contacts from an uploaded CSV file (base64 data URL)
   */
  importContactsFile: async (data: {
    file: string;
    provider: 'gmail' | 'icloud' | 'outlook' | 'yahoo' | 'linkedin' | 'other';
  }): Promise<{ success: boolean; message?: string }> => {
    return apiClient.post('/import-contacts', data, { requireAuth: true });
  },

  /**
   * Get saved job applications
   */
  getApplications: async (): Promise<unknown[]> => {
    const rawResponse = await apiClient.get<unknown[] | { data: unknown[] }>('/member/applications', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Delete/withdraw a saved job application
   */
  deleteApplication: async (id: string | number): Promise<{ success: boolean; message?: string }> => {
    return apiClient.delete(`/member/applications/${id}`, { requireAuth: true });
  },

  /**
   * Get per-venue newsletter subscriptions
   */
  getNewsletters: async (): Promise<unknown[]> => {
    const rawResponse = await apiClient.get<unknown[] | { data: unknown[] }>('/member/newsletter', { requireAuth: true });
    return unwrapResponse(rawResponse);
  },

  /**
   * Toggle subscription status for a venue's newsletter
   */
  toggleNewsletterStatus: async (data: { venueId: string | number }): Promise<{ success: boolean; message?: string }> => {
    return apiClient.post('/member/newsletter/toggle-status', data, { requireAuth: true });
  },
};

export default memberApi;
